import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TenantHealthLeaderboard = ({ onTenantSelect }) => {
  const [sortBy, setSortBy] = useState('health'); // 'health', 'revenue', 'risk'

  const tenantHealthData = [
    {
      id: 'tenant-001',
      companyName: 'TechCorp Solutions',
      logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=64&h=64&fit=crop&crop=center',
      healthScore: 95,
      riskLevel: 'low',
      monthlyRevenue: 2500,
      userCount: 45,
      lastActivity: '2 hours ago',
      renewalDate: '2025-03-15',
      daysToRenewal: 48,
      trend: 'up'
    },
    {
      id: 'tenant-002',
      companyName: 'Digital Dynamics',
      logo: 'https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=64&h=64&fit=crop&crop=center',
      healthScore: 92,
      riskLevel: 'low',
      monthlyRevenue: 1800,
      userCount: 32,
      lastActivity: '5 hours ago',
      renewalDate: '2025-04-22',
      daysToRenewal: 86,
      trend: 'up'
    },
    {
      id: 'tenant-003',
      companyName: 'Innovation Labs',
      logo: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=64&h=64&fit=crop&crop=center',
      healthScore: 88,
      riskLevel: 'medium',
      monthlyRevenue: 3200,
      userCount: 67,
      lastActivity: '1 day ago',
      renewalDate: '2025-02-28',
      daysToRenewal: 33,
      trend: 'stable'
    },
    {
      id: 'tenant-004',
      companyName: 'StartupVenture',
      logo: 'https://images.unsplash.com/photo-1553484771-371a605b060b?w=64&h=64&fit=crop&crop=center',
      healthScore: 76,
      riskLevel: 'high',
      monthlyRevenue: 950,
      userCount: 18,
      lastActivity: '3 days ago',
      renewalDate: '2025-01-30',
      daysToRenewal: 4,
      trend: 'down'
    },
    {
      id: 'tenant-005',
      companyName: 'Enterprise Global',
      logo: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=64&h=64&fit=crop&crop=center',
      healthScore: 94,
      riskLevel: 'low',
      monthlyRevenue: 5500,
      userCount: 124,
      lastActivity: '30 minutes ago',
      renewalDate: '2025-06-15',
      daysToRenewal: 140,
      trend: 'up'
    },
    {
      id: 'tenant-006',
      companyName: 'CloudFirst Inc',
      logo: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=64&h=64&fit=crop&crop=center',
      healthScore: 82,
      riskLevel: 'medium',
      monthlyRevenue: 2100,
      userCount: 38,
      lastActivity: '6 hours ago',
      renewalDate: '2025-03-08',
      daysToRenewal: 41,
      trend: 'stable'
    }
  ];

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'text-success bg-success/10';
      case 'medium': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      default: return 'text-text-secondary bg-muted';
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return { icon: 'TrendingUp', color: 'text-success' };
      case 'down': return { icon: 'TrendingDown', color: 'text-error' };
      default: return { icon: 'Minus', color: 'text-text-secondary' };
    }
  };

  const sortedData = [...tenantHealthData].sort((a, b) => {
    switch (sortBy) {
      case 'health':
        return b.healthScore - a.healthScore;
      case 'revenue':
        return b.monthlyRevenue - a.monthlyRevenue;
      case 'risk':
        const riskOrder = { high: 3, medium: 2, low: 1 };
        return riskOrder[b.riskLevel] - riskOrder[a.riskLevel];
      default:
        return 0;
    }
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-1">
            Tenant Health Leaderboard
          </h3>
          <p className="text-sm text-text-secondary">
            Top performing tenants with risk indicators
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-input border border-border rounded-md px-3 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="health">Health Score</option>
            <option value="revenue">Revenue</option>
            <option value="risk">Risk Level</option>
          </select>
          
          <Button variant="outline" size="sm">
            <Icon name="MoreVertical" size={16} />
          </Button>
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="space-y-3">
        {sortedData.map((tenant, index) => {
          const trendInfo = getTrendIcon(tenant.trend);
          
          return (
            <div
              key={tenant.id}
              onClick={() => onTenantSelect(tenant)}
              className="flex items-center justify-between p-4 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 cursor-pointer transition-all duration-200"
            >
              {/* Left Section - Company Info */}
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <span className={`
                    text-sm font-bold w-6 h-6 rounded-full flex items-center justify-center
                    ${index < 3 ? 'bg-primary text-primary-foreground' : 'bg-muted text-text-secondary'}
                  `}>
                    {index + 1}
                  </span>
                  
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                    <img
                      src={tenant.logo}
                      alt={tenant.companyName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/assets/images/no_image.png';
                      }}
                    />
                  </div>
                </div>
                
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-text-primary truncate">
                    {tenant.companyName}
                  </h4>
                  <div className="flex items-center space-x-3 text-xs text-text-secondary">
                    <span>{tenant.userCount} users</span>
                    <span>•</span>
                    <span>{tenant.lastActivity}</span>
                  </div>
                </div>
              </div>

              {/* Right Section - Metrics */}
              <div className="flex items-center space-x-6">
                {/* Health Score */}
                <div className="text-center">
                  <div className={`text-lg font-bold ${getHealthScoreColor(tenant.healthScore)}`}>
                    {tenant.healthScore}
                  </div>
                  <div className="text-xs text-text-secondary">Health</div>
                </div>

                {/* Revenue */}
                <div className="text-center">
                  <div className="text-sm font-semibold text-text-primary">
                    {formatCurrency(tenant.monthlyRevenue)}
                  </div>
                  <div className="text-xs text-text-secondary">MRR</div>
                </div>

                {/* Risk & Trend */}
                <div className="flex items-center space-x-2">
                  <span className={`
                    px-2 py-1 rounded-full text-xs font-medium capitalize
                    ${getRiskColor(tenant.riskLevel)}
                  `}>
                    {tenant.riskLevel}
                  </span>
                  
                  <Icon 
                    name={trendInfo.icon} 
                    size={16} 
                    className={trendInfo.color}
                  />
                </div>

                {/* Renewal Indicator */}
                <div className="text-center">
                  <div className={`text-sm font-medium ${
                    tenant.daysToRenewal <= 30 ? 'text-error' : 
                    tenant.daysToRenewal <= 60 ? 'text-warning' : 'text-text-primary'
                  }`}>
                    {tenant.daysToRenewal}d
                  </div>
                  <div className="text-xs text-text-secondary">Renewal</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">
            Showing top {sortedData.length} tenants
          </span>
          <Button variant="ghost" size="sm" className="text-primary">
            View All Tenants
            <Icon name="ArrowRight" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TenantHealthLeaderboard;